import classNames from "classnames";
import type { ReactNode } from "react";
import React from "react";

import Head from "./Head";
import Row from "./Row";
import LoadingRow from "./Row/Loading";
import type { TableColumn, TableRow, TableSortOptions } from "./types";

export interface ITable {
    id: string;
    title?: string;
    columns: TableColumn[];
    rows: TableRow[];
    sort?: TableSortOptions;
    onColumnClick?: (args: { id: string }) => void;
}

export interface ICollapsableTable extends ITable {
    pinnedRowIds?: string[];
    isCollapsed?: boolean;
    onRowClick?: (args: { row: TableRow; tableId: string }) => void;
    className?: string;
    isPending?: boolean;
    isAnimated?: boolean;
}

function TableTemplate({
    className,
    Header,
    children,
}: {
    className?: string;
    Header?: JSX.Element;
    children: JSX.Element | JSX.Element[];
}) {
    return (
        <div className={`relative flex flex-col gap-1 ${className}`}>
            <div className={`relative border-[0px]! border-[#eaeaea]! rounded-[3px]!`}>
                <table className="w-full! text-center! table-fixed! ">
                    {Header}
                    <tbody>{children}</tbody>
                </table>
            </div>
        </div>
    );
}

function Table(props: ICollapsableTable) {
    const { body, footer, pinned } = props.rows.reduce<{
        body: ReactNode[];
        footer: ReactNode[];
        pinned: ReactNode[];
    }>(
        (acc, row) => {
            const isPinned = props.pinnedRowIds?.includes?.(row.id);

            const RowComponent = (
                <Row
                    key={row.id}
                    // fixed height to simplify collapse animation
                    className="border-x-[0px]! border-[#eaeaea]! h-[32px] overflow-hidden"
                    data={row}
                    columns={props.columns}
                    isPinned={isPinned}
                    onClick={() => props.onRowClick?.({ row, tableId: props.id })}
                />
            );

            if (row.placement === "footer") {
                acc.footer.push(RowComponent);
            } else {
                acc.body.push(RowComponent);
            }

            if (isPinned) {
                acc.pinned.push(RowComponent);
            }

            return acc;
        },
        { body: [], footer: [], pinned: [] },
    );

    if (props.isPending) {
        body.push(
            ...new Array(props.rows.length ? 2 : 5).fill(null).map((_, i) => {
                return <LoadingRow key={`loading-${i}`} columns={props.columns} className="h-[32px] overflow-hidden" />;
            }),
        );
    }

    return (
        <TableTemplate Header={<Head columns={props.columns} sort={props.sort} onColumnClick={props.onColumnClick} />}>
            <>
                <div
                    className={classNames(
                        "overflow-hidden",
                        {
                            "transition-all": !!props.rows.length && !!props.isAnimated,
                        },
                        "border-[1px]! border-t-[0px]! rounded-b-[3px]! border-[#eaeaea]!",
                    )}
                    style={{
                        // each row is 32px (fixed height set above, to simplify animation)
                        height: props.isCollapsed ? "0px" : body.length * 32 + "px",
                    }}
                >
                    {body}
                </div>
                <div className="sticky bottom-0 bg-white z-[8]!">
                    <div className="rounded-[3px]! border-[#eaeaea]! border-[1px]! overflow-hidden">{footer}</div>
                </div>
                {pinned.length ? (
                    <div className="mt-3! border-[1px]! rounded-[3px]! border-[#eaeaea]! bg-info-light/50">
                        <div className="">{pinned}</div>
                    </div>
                ) : null}
            </>
        </TableTemplate>
    );
}

export default React.memo(Table);
